import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  ThemeProvider,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Google,
  Mail,
  Lock,
  Visibility,
  VisibilityOff,
  WavingHand,
} from '@mui/icons-material';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { useSpring, animated, config } from 'react-spring';

import AuthService from './auth.service';
import AlertService from '../../components/Alerts/alerts';
import ConfigurationService from '../Configuration/configuration.service';
import backgroundImage from '../../assets/backgroundImage.png';

export default function Login() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [logo, setLogo] = useState(localStorage.getItem('system_logo'));
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [googleClientId, setGoogleClientId] = useState('');
  const [theme, setTheme] = useState(createTheme());

  // Add responsive breakpoints
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:960px)');

  const fadeIn = useSpring({
    opacity: 1,
    from: { opacity: 0 },
    config: config.molasses,
  });

  const slideUp = useSpring({
    transform: 'translateY(0px)',
    from: { transform: 'translateY(50px)' },
    config: config.wobbly,
  });


  useEffect(() => {
    getSetLanguage();
    getSystemConfig();
  }, []);

  const getSetLanguage = () => {
    const language = localStorage.getItem('language') || 'en';
    i18n.changeLanguage(language);
    localStorage.setItem('language', language);
  };

  const getSystemConfig = async () => {
    try {
      const response = await ConfigurationService.getConfiguration();
      if (response.data.status) {
        const config = response.data.data[0];
        setColors(config.buttons_color, '--buttons-color');
        setColors(config.topbar_color, '--topbar-color');
        setColors(config.leftbar_color, '--leftbar-color');
        setColors(config.buttons_text_color, '--buttons-text-color');
        setSystemLogo(config.system_logo);
        localStorage.setItem('google_api_key', config.google_api_key);

        setTheme(createTheme({
          palette: {
            primary: {
              main: config.buttons_color,
            },
            secondary: {
              main: config.topbar_color,
            },
          },
        }));
      } else {
        setColors('#3488A4', 'onerror');
      }
    } catch (error) {
      setColors('#3488A4', 'onerror');
    }
  };

  const setColors = (color, param) => {
    localStorage.setItem(param.slice(2), color);
    document.documentElement.style.setProperty(param, color);
  };

  const setSystemLogo = (sys_logo) => {
    setLogo(sys_logo);
    localStorage.setItem('system_logo', sys_logo);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      AlertService.swalErrorAlert(t('Please fill in all fields'));
      return;
    }

    setLoading(true);
    try {
      await AuthService.login(email, password, 'customers');
      // Ensure user_type is set explicitly even if the login service didn't persist it.
      localStorage.setItem('user_type', 'customers');
      setLoading(false);
      AlertService.successToast('Login Successful.');
      navigate('/Store');
      window.location.reload();
    } catch (error) {
      setLoading(false);
      AlertService.swalErrorAlert(t('Invalid Credentials'));
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      const payload = {
        functionName: 'GoogleLogin',
        postData: {
          email: decoded.email,
          firstName: decoded.given_name,
          lastName: decoded.family_name,
          picture: decoded.picture,
          googleId: decoded.sub,
          business_id: '6698a715b67d920e38e7d1ab',
          user_type: 'customers'
        }
      };

      setLoading(true);
      await AuthService.GoogleLogin(payload);
      localStorage.setItem('user_type', 'customers');
      setLoading(false);
      AlertService.successToast('Login Successful.');
      navigate('/Store');
      window.location.reload();
    } catch (error) {
      setLoading(false);
      AlertService.swalErrorAlert(t('Invalid Credentials'));
    }
  };

  const handleGoogleError = () => {
    AlertService.swalErrorAlert(t('Google Sign-In Failed'));
  };

  return (
    <ThemeProvider theme={theme}>
      <GoogleOAuthProvider clientId={googleClientId || '20989683412-dej36jcnq185ul4i91gdr1q2omgijldi.apps.googleusercontent.com'}>
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundImage: `linear-gradient(to left, rgba(255,255,255,0.3), rgba(255,255,255,0.1)), url(${backgroundImage})`,
            backgroundSize: 'cover',
            p: { xs: 2, sm: 3 },
            overflow: 'hidden',
          }}
        >
          <Container maxWidth="lg">
            <animated.div style={fadeIn}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  backgroundColor: '#fff',
                  borderRadius: { xs: '12px', sm: '20px' },
                  overflow: 'hidden',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                  height: { xs: 'auto', md: '600px' },
                }}
              >
                {/* Left Side - Login Form */}
                <Box
                  sx={{
                    flex: 1,
                    p: { xs: 3, sm: 4, md: 6 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: { xs: 2, sm: 3 }
                  }}
                >
                  {/* Logo */}
                  <animated.div style={slideUp}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        alignSelf: { xs: 'center', sm: 'flex-start' }
                      }}
                    >
                      <Box
                        component="img"
                        src={logo}
                        alt="Logo"
                        sx={{
                          marginTop: '-5%',
                          width: { xs: 40, sm: 50, md: 60 },
                          height: { xs: 40, sm: 50, md: 60 },
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          fontSize: { xs: '1.1rem', sm: '1.25rem' }
                        }}
                      >
                        Online Store
                      </Typography>
                    </Box>
                  </animated.div>

                  <animated.div style={slideUp}>
                    <Typography
                      variant="h4"
                      sx={{
                        color: theme.palette.primary.main,
                        mb: { xs: 1, sm: 2, md: 3 },
                        fontWeight: 500,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                        textAlign: { xs: 'center', sm: 'left' }
                      }}
                    >
                      {/* Welcome Back! <WavingHand color="primary" /> */}
                    </Typography>
                  </animated.div>

                  {/* Google Login Button */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                    <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: 400 } }}>
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        useOneTap
                        shape="rectangular"
                        width="100%"
                        theme="filled_blue"
                        text="continue_with"
                        render={({ onClick, disabled }) => (
                          <Button
                            onClick={onClick}
                            disabled={disabled || loading}
                            fullWidth
                            variant="outlined"
                            startIcon={<Google />}
                            sx={{
                              mt: { xs: 1, sm: 2 },
                              mb: { xs: 1, sm: 2 },
                              py: { xs: 1, sm: 1.5 },
                              borderRadius: '50px',
                              transition: 'all 0.3s',
                              '&:hover': {
                                transform: 'scale(1.02)',
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                              },
                            }}
                          >
                            Continue with Google
                          </Button>
                        )}
                      />
                    </Box>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    or use your email account
                  </Typography>

                  {/* Login Form */}
                  <Box
                    component="form"
                    onSubmit={onSubmit}
                    sx={{
                      width: '100%',
                      maxWidth: { xs: '100%', sm: 400 }
                    }}
                  >
                    <TextField
                      fullWidth
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      sx={{
                        mb: { xs: 1.5, sm: 2 },
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#f8f9fa',
                          '& fieldset': { border: 'none' },
                          borderRadius: '50px',
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Mail sx={{ color: 'text.secondary', fontSize: { xs: 18, sm: 24 } }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      sx={{
                        mb: { xs: 1.5, sm: 2 },
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#f8f9fa',
                          '& fieldset': { border: 'none' },
                          borderRadius: '50px',
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: 'text.secondary', fontSize: { xs: 18, sm: 24 } }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              sx={{ padding: { xs: 0.5, sm: 1 } }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Button
                      fullWidth
                      variant="contained"
                      type="submit"
                      disabled={loading}
                      sx={{
                        py: { xs: 1, sm: 1.5 },
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: '50px',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                        },
                        transition: 'all 0.3s'
                      }}
                    >
                      {loading ? 'Signing in...' : 'SIGN IN'}
                    </Button>
                    <Button
                      sx={{
                        display: 'block',
                        margin: '0 auto',
                        mb: { xs: 2, sm: 3 },
                        color: 'text.secondary',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: theme.palette.primary.main,
                        }
                      }}
                      onClick={() => navigate('/customer_forgot_password')}
                    >
                      Forgot your password?
                    </Button>
                  </Box>
                </Box>

                {/* Right Side - Sign Up CTA */}
                <Box
                  sx={{
                    flex: 1,
                    bgcolor: theme.palette.primary.main,
                    p: { xs: 4, sm: 6 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    position: 'relative',
                    overflow: 'hidden',
                    minHeight: { xs: '300px', md: 'auto' },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '0',
                      right: '0',
                      width: { xs: '200px', sm: '300px' },
                      height: { xs: '200px', sm: '300px' },
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
                      transform: 'translate(50%, -50%) rotate(45deg)',
                      borderRadius: '50px'
                    }
                  }}
                >
                  <animated.div style={slideUp}>
                    <Typography
                      variant="h3"
                      sx={{
                        mb: { xs: 2, sm: 3 },
                        fontWeight: 500,
                        fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                        textAlign: 'center'
                      }}
                    >
                      Welcome to IPH Pharmaceuticals Online Store
                    </Typography>
                  </animated.div>
                  {/* <animated.div style={slideUp}>
                    <Typography
                      textAlign="center"
                      sx={{
                        mb: { xs: 3, sm: 4 },
                        maxWidth: 400,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        px: { xs: 2, sm: 0 }
                      }}
                    >
                      Enter your personal details and start journey with us
                    </Typography>
                  </animated.div> */}
                  <animated.div style={useSpring({
                    from: { opacity: 0, transform: 'scale(0.5)' },
                    to: { opacity: 1, transform: 'scale(1)' },
                    config: config.wobbly,
                  })}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/customer_register')}
                      sx={{
                        color: 'white',
                        borderColor: 'white',
                        borderRadius: '50px',
                        px: { xs: 4, sm: 6 },
                        py: { xs: 1, sm: 1.5 },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.3s'
                      }}
                    >
                      Create An Account
                    </Button>
                  </animated.div>
                </Box>
              </Box>
            </animated.div>
          </Container>
        </Box>
      </GoogleOAuthProvider>
    </ThemeProvider >
  );
}
